import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Pagination,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button } from "@mui/material";
import { baseapi } from "./Environmentalurl";

export default function LogsPage() {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [fileSize, setFileSize] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    getFiles();
  }, []);

  // ==========================
  // GET FILE LIST
  // ==========================
  const getFiles = async () => {
    try {
      setFileLoading(true);

      const response = await axios.get(
        `${baseapi}/auth/list`,
      );

      if (response.data.success) {
        setFiles(response.data.files || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFileLoading(false);
    }
  };

  // ==========================
  // READ LOG FILE
  // ==========================
  const getLogs = async (path, pageNo = 0) => {
    try {
      setLogLoading(true);

      const response = await axios.post(
       `${baseapi}/auth/read-log?page=${pageNo}&size=5000`,
        {
          path,
        },
      );

      if (response.data.success) {
        setLogs(response.data.content || []);
        setPage(response.data.currentPage || 0);
        setTotalPages(response.data.totalPages || 0);

        setFileSize(response.data.fileSizeKB || "");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLogLoading(false);
    }
  };

  // ==========================
  // FILE SELECT
  // ==========================
  const handleFileChange = async (event) => {
    const selectedPath = event.target.value;

    const selected = files.find((file) => file.path === selectedPath);

    setSelectedFile(selected);

    setPage(0);

    await getLogs(selectedPath, 0);
  };

  // ==========================
  // PAGE CHANGE
  // ==========================
  const handlePageChange = async (event, selectedPage) => {
    const pageNo = selectedPage - 1;

    setPage(pageNo);

    await getLogs(selectedFile.path, pageNo);
  };

  const handleCopyLogs = async () => {
    try {
      await navigator.clipboard.writeText(logs.join("\n"));
      alert("Logs copied successfully");
    } catch (error) {
      console.error(error);
      alert("Failed to copy logs");
    }
  };
  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Application Logs
      </Typography>

      {/* SELECT FILE */}
      <FormControl fullWidth disabled={fileLoading}>
        <InputLabel>Select Log File</InputLabel>

        <Select
          value={selectedFile?.path || ""}
          label="Select Log File"
          onChange={handleFileChange}
        >
          {files
            .filter((file) => file.type === "FILE")
            .map((file) => (
              <MenuItem key={file.path} value={file.path}>
                {file.lastModified}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* FILE DETAILS */}
      {selectedFile && (
        <Paper
          elevation={2}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography>
            <strong>File Name:</strong> {selectedFile.name}
          </Typography>

          <Typography>
            <strong>Size:</strong> {selectedFile.size}
          </Typography>

          <Typography>
            <strong>Last Modified:</strong> {selectedFile.lastModified}
          </Typography>

          <Typography>
            <strong>Path:</strong> {selectedFile.path}
          </Typography>
        </Paper>
      )}

      {/* PAGINATION */}
      {totalPages > 0 && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Page {page + 1} of {totalPages}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              File Size: {fileSize}
            </Typography>
          </Box>

          <Pagination
            count={totalPages}
            page={page + 1}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            onChange={handlePageChange}
          />

          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLogs}
          >
            Copy Logs
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* LOADING */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          elevation={3}
          sx={{
            height: "650px",
            overflowY: "auto",
            p: 2,
            backgroundColor: "#111827",
            color: "#22c55e",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            borderRadius: 2,
            position: "relative",
          }}
        >
          {logLoading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress color="success" />
            </Box>
          ) : logs.length > 0 ? (
            logs.map((line, index) => (
              <Typography
                key={index}
                component="div"
                sx={{
                  fontSize: "12px",
                  lineHeight: 1.7,
                  mb: 0.4,
                }}
              >
                {line}
              </Typography>
            ))
          ) : (
            <Typography>No log data available</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
